/**
 * EntityPickerBase — Reusable combobox picker for FK fields with optional inline create.
 *
 * Handles the Popover + Command shell, search, clear, and "Add New" action.
 * Entity-specific pickers (OrganizationPicker, ContactPicker) wrap this with their
 * own data fetching, item rendering, and Quick Create Dialog.
 *
 * See docs/design-system/ui-detail-and-form-pages.md § FK Picker with Inline Create.
 */
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronsUpDown, ListFilter, Pencil, Plus, type LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';

export interface EntityPickerBaseProps<T extends { id: string }> {
  /** Currently selected entity ID */
  value: string;
  /** List of entities to display */
  items: T[];

  // --- Display ---
  /** Get display label for an entity (shown in trigger when selected) */
  getItemLabel: (item: T) => string;
  /** Render the content of each dropdown item */
  renderItem: (item: T, isSelected: boolean) => ReactNode;
  /** Optional label above the picker */
  label?: string;
  /** Placeholder shown in the trigger button when nothing is selected */
  placeholder: string;
  /** Placeholder inside the search input */
  searchPlaceholder: string;
  /** Message when search yields no results */
  emptyMessage: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes on the wrapper */
  className?: string;
  /** Show required indicator on label */
  required?: boolean;
  /** id applied to the trigger button (so an external <Label htmlFor> can target it) */
  triggerId?: string;
  /** Render the trigger with a destructive border (validation error) */
  hasError?: boolean;
  /**
   * Human-readable name of the scope this picker is filtered to (e.g. an
   * organization or customer name). When set, a muted "Filtered to {name}"
   * header is shown at the top of the dropdown so the user knows the list — and
   * any entity they create — is scoped to that context, not the whole tenant.
   */
  contextLabel?: string | null;

  // --- Search ---
  /** Current search text (controlled) */
  search: string;
  /** Search text change handler */
  onSearchChange: (search: string) => void;

  // --- Popover state ---
  /** Whether the popover is open (controlled) */
  open: boolean;
  /** Popover open/close handler */
  onOpenChange: (open: boolean) => void;

  // --- Selection ---
  /** Called when an item is selected */
  onSelect: (id: string) => void;
  /** Show "None" option to clear selection */
  allowClear?: boolean;
  /** Called when the clear option is selected */
  onClear?: () => void;
  /** Custom label for the clear option (defaults to "None"). Useful for "All …" semantics. */
  clearLabel?: string;

  // --- Create action ---
  /** Hide the "Add New" action (useful in filter contexts) */
  hideAddNew?: boolean;
  /** Label for the "Add New" action */
  addNewLabel: string;
  /** Called when the "Add New" action is clicked */
  onAddNew: () => void;

  // --- Entity link ---
  /** URL to the selected entity's detail page. When set, the selected label becomes a clickable link with a pencil edit icon. */
  selectedEntityUrl?: string | null;
  /** Icon for the entity type, shown next to the link in linked trigger mode */
  entityIcon?: LucideIcon;

  // --- Popover styling ---
  /** Additional CSS classes for the PopoverContent (e.g., min-width override) */
  popoverClassName?: string;
}

export function EntityPickerBase<T extends { id: string }>({
  value,
  items,
  getItemLabel,
  renderItem,
  label,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  disabled,
  className,
  required,
  triggerId,
  hasError,
  contextLabel,
  search,
  onSearchChange,
  open,
  onOpenChange,
  onSelect,
  allowClear,
  onClear,
  clearLabel,
  hideAddNew = false,
  addNewLabel,
  onAddNew,
  selectedEntityUrl,
  entityIcon: EntityIcon,
  popoverClassName,
}: EntityPickerBaseProps<T>) {
  const { t } = useTranslation('common');
  const [hovered, setHovered] = useState(false);

  const selectedItem = items.find((item) => item.id === value);

  // Cache the selected item's label so the linked trigger stays stable
  // even when search filtering removes the selected item from `items`
  const cachedLabelRef = useRef<{ id: string; label: string } | null>(null);
  if (selectedItem) {
    cachedLabelRef.current = { id: value, label: getItemLabel(selectedItem) };
  } else if (cachedLabelRef.current?.id !== value) {
    cachedLabelRef.current = null;
  }
  const selectedLabel = selectedItem
    ? getItemLabel(selectedItem)
    : cachedLabelRef.current?.label;

  // When selectedEntityUrl is provided and an item is selected, render inline link + edit trigger
  const showLinkedTrigger = selectedEntityUrl && value && selectedLabel && !disabled;

  // Contextual filter header — shown at the top of the dropdown when the picker
  // is scoped to a named entity, so the user sees the active filter and knows a
  // newly created entity inherits that scope.
  const contextHeader = contextLabel ? (
    <div className="flex items-center gap-1.5 border-b px-3 py-2 text-xs text-muted-foreground">
      <ListFilter className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">
        {t('app.filteredToContext', { name: contextLabel })}
      </span>
    </div>
  ) : null;

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <Label className="mb-1.5 block">
          {label}{required && ' *'}
        </Label>
      )}
      {showLinkedTrigger ? (
        <div
          className="relative group"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div
            className={cn(
              'flex w-full items-center rounded-md px-3 text-sm h-10 py-2',
              'transition-colors duration-150',
              'cursor-pointer',
              hovered && !open && 'bg-muted/50',
              open && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
            )}
          >
            {EntityIcon && (
              <EntityIcon className="h-4 w-4 me-2 flex-shrink-0 text-muted-foreground" />
            )}
            <Link
              to={selectedEntityUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 truncate text-primary hover:underline inline-flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="truncate">{selectedLabel}</span>
              <ArrowUpRight className="h-3 w-3 flex-shrink-0 opacity-60" />
            </Link>
            <Popover open={open} onOpenChange={onOpenChange}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'ms-2 p-1 rounded hover:bg-muted transition-colors flex-shrink-0',
                    hovered || open ? 'opacity-100' : 'opacity-0'
                  )}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onOpenChange(true);
                  }}
                >
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent className={cn("p-0 w-[280px]", popoverClassName)} align="start">
                <Command shouldFilter={false}>
                  {contextHeader}
                  <CommandInput
                    placeholder={searchPlaceholder}
                    value={search}
                    onValueChange={onSearchChange}
                  />
                  <CommandList>
                    <CommandEmpty>{emptyMessage}</CommandEmpty>

                    {allowClear && (
                      <CommandGroup>
                        <CommandItem value="__clear__" onSelect={onClear}>
                          <span className="text-muted-foreground">{clearLabel ?? t('app.none')}</span>
                        </CommandItem>
                      </CommandGroup>
                    )}

                    {items.length > 0 && (
                      <>
                        {allowClear && <CommandSeparator />}
                        <CommandGroup>
                          {items.map((item) => (
                            <CommandItem
                              key={item.id}
                              value={item.id}
                              onSelect={() => onSelect(item.id)}
                            >
                              {renderItem(item, item.id === value)}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}

                    {!hideAddNew && (
                      <>
                        <CommandSeparator />
                        <CommandGroup>
                          <CommandItem onSelect={onAddNew} className="text-primary">
                            <Plus className="me-2 h-4 w-4" />
                            {addNewLabel}
                          </CommandItem>
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      ) : (
        <Popover open={open} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button
              id={triggerId}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                'w-full justify-between font-normal',
                !value && 'text-muted-foreground',
                hasError && 'border-destructive',
              )}
            >
              <span className="truncate">
                {selectedLabel ?? placeholder}
              </span>
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className={cn("p-0 w-[--radix-popover-trigger-width]", popoverClassName)} align="start">
            <Command shouldFilter={false}>
              {contextHeader}
              <CommandInput
                placeholder={searchPlaceholder}
                value={search}
                onValueChange={onSearchChange}
              />
              <CommandList>
                <CommandEmpty>{emptyMessage}</CommandEmpty>

                {allowClear && (
                  <CommandGroup>
                    <CommandItem value="__clear__" onSelect={onClear}>
                      <span className="text-muted-foreground">{clearLabel ?? t('app.none')}</span>
                    </CommandItem>
                  </CommandGroup>
                )}

                {items.length > 0 && (
                  <>
                    {allowClear && <CommandSeparator />}
                    <CommandGroup>
                      {items.map((item) => (
                        <CommandItem
                          key={item.id}
                          value={item.id}
                          onSelect={() => onSelect(item.id)}
                        >
                          {renderItem(item, item.id === value)}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}

                {!hideAddNew && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem onSelect={onAddNew} className="text-primary">
                        <Plus className="me-2 h-4 w-4" />
                        {addNewLabel}
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
